import { Link } from 'react-router-dom';
import Navbar from '../../../components/feature/Navbar';
import Footer from '../../../components/feature/Footer';
import SEOHead from '../../../components/base/SEOHead';

export default function TexasSecretaryOfStatePage() {
  return (
    <>
      <SEOHead
        title="Texas Secretary of State Business Registration & Filing Guide"
        description="Complete guide to Texas Secretary of State business registration and filing services. Learn about entity formation, assumed names, annual reports, and online filing through SOSDirect for Texas businesses."
        canonical="/regulations/texas-secretary-of-state"
        keywords="Texas Secretary of State, SOSDirect, Texas business registration, entity formation Texas, assumed name Texas, Texas annual report"
        ogImage="https://public.readdy.ai/ai/img_res/da3c77da16a4984c3064a5aa55e7648c.jpg"
      />
      <div className="min-h-screen bg-white">
        <Navbar />
        
        <main className="pt-32 pb-20">
          <div className="max-w-7xl mx-auto px-6">
            {/* Breadcrumb */}
            <nav aria-label="Breadcrumb" className="mb-8">
              <Link to="/regulations" className="text-[#D2691E] hover:underline text-sm font-medium">
                ← Back to Regulations
              </Link>
            </nav>

            {/* Breadcrumb Structured Data */}
            <script type="application/ld+json">
              {JSON.stringify({
                "@context": "https://schema.org",
                "@type": "BreadcrumbList",
                "itemListElement": [
                  {
                    "@type": "ListItem",
                    "position": 1,
                    "name": "Home",
                    "item": "https://elpasotexasbusiness.com/"
                  },
                  {
                    "@type": "ListItem",
                    "position": 2,
                    "name": "Regulations",
                    "item": "https://elpasotexasbusiness.com/regulations"
                  },
                  {
                    "@type": "ListItem",
                    "position": 3,
                    "name": "Texas Secretary of State",
                    "item": "https://elpasotexasbusiness.com/regulations/texas-secretary-of-state"
                  }
                ]
              })}
            </script>

            {/* Header */}
            <div className="mb-12">
              <div className="flex items-center space-x-4 mb-4">
                <div className="w-16 h-16 bg-gradient-to-br from-[#1B3A3A] to-[#2C5F4F] rounded-2xl flex items-center justify-center">
                  <i className="ri-government-line text-white text-3xl"></i>
                </div>
                <div>
                  <h1 className="text-4xl font-bold text-gray-900">Texas Secretary of State</h1>
                  <p className="text-lg text-gray-600 mt-2">Business entity registration and filing services</p>
                </div>
              </div>
            </div>

            {/* Official Website Link */}
            <div className="bg-gradient-to-br from-[#1B3A3A] to-[#2C5F4F] rounded-2xl p-8 mb-12">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-2xl font-bold text-white mb-2">Official Website</h3>
                  <p className="text-white/80">Visit the Texas Secretary of State for official forms and filing</p>
                </div>
                <a
                  href="https://www.sos.texas.gov"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="bg-white text-[#1B3A3A] px-8 py-3 rounded-xl font-semibold hover:bg-[#FFE8D6] transition-all cursor-pointer flex items-center space-x-2 whitespace-nowrap"
                >
                  <span>Visit SOS.Texas.gov</span>
                  <i className="ri-external-link-line"></i>
                </a>
              </div>
            </div>

            {/* Business Entity Formation */}
            <section className="mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-6">Business Entity Formation</h2>
              <div className="bg-gray-50 rounded-2xl p-8">
                <p className="text-gray-700 leading-relaxed mb-6">
                  The Texas Secretary of State is responsible for registering and maintaining records for all business entities in Texas. Whether you're forming an LLC, corporation, partnership, or other business structure, you'll need to file with the SOS.
                </p>
                
                <h3 className="text-xl font-bold text-gray-900 mb-4">Available Business Structures</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                  <div className="bg-white rounded-xl p-6 border border-gray-200">
                    <h4 className="font-bold text-gray-900 mb-2">Limited Liability Company (LLC)</h4>
                    <p className="text-sm text-gray-600">Flexible structure with liability protection and pass-through taxation</p>
                  </div>
                  <div className="bg-white rounded-xl p-6 border border-gray-200">
                    <h4 className="font-bold text-gray-900 mb-2">Corporation (C-Corp or S-Corp)</h4>
                    <p className="text-sm text-gray-600">Formal structure with shareholders, directors, and officers</p>
                  </div>
                  <div className="bg-white rounded-xl p-6 border border-gray-200">
                    <h4 className="font-bold text-gray-900 mb-2">General Partnership</h4>
                    <p className="text-sm text-gray-600">Two or more owners sharing profits and liabilities</p>
                  </div>
                  <div className="bg-white rounded-xl p-6 border border-gray-200">
                    <h4 className="font-bold text-gray-900 mb-2">Limited Partnership (LP)</h4>
                    <p className="text-sm text-gray-600">General partners manage, limited partners invest</p>
                  </div>
                </div>
              </div>
            </section>

            {/* SOSDirect Portal */}
            <section className="mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-6">SOSDirect Online Filing System</h2>
              <div className="bg-gray-50 rounded-2xl p-8">
                <p className="text-gray-700 leading-relaxed mb-6">
                  SOSDirect is the Texas Secretary of State's online filing system that allows you to file business documents electronically, search business records, and manage your entity information 24/7.
                </p>
                
                <h3 className="text-xl font-bold text-gray-900 mb-4">What You Can Do on SOSDirect</h3>
                <ul className="space-y-3">
                  <li className="flex items-start space-x-3">
                    <div className="w-6 h-6 bg-[#2C5F4F] rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                      <i className="ri-check-line text-white text-sm"></i>
                    </div>
                    <span className="text-gray-700">File formation documents for new business entities</span>
                  </li>
                  <li className="flex items-start space-x-3">
                    <div className="w-6 h-6 bg-[#2C5F4F] rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                      <i className="ri-check-line text-white text-sm"></i>
                    </div>
                    <span className="text-gray-700">Submit amendments, mergers, and conversions</span>
                  </li>
                  <li className="flex items-start space-x-3">
                    <div className="w-6 h-6 bg-[#2C5F4F] rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                      <i className="ri-check-line text-white text-sm"></i>
                    </div>
                    <span className="text-gray-700">File periodic reports and franchise tax reports</span>
                  </li>
                  <li className="flex items-start space-x-3">
                    <div className="w-6 h-6 bg-[#2C5F4F] rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                      <i className="ri-check-line text-white text-sm"></i>
                    </div>
                    <span className="text-gray-700">Search existing business entity records</span>
                  </li>
                  <li className="flex items-start space-x-3">
                    <div className="w-6 h-6 bg-[#2C5F4F] rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                      <i className="ri-check-line text-white text-sm"></i>
                    </div>
                    <span className="text-gray-700">Order certificates of filing and certified copies</span>
                  </li>
                  <li className="flex items-start space-x-3">
                    <div className="w-6 h-6 bg-[#2C5F4F] rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                      <i className="ri-check-line text-white text-sm"></i>
                    </div>
                    <span className="text-gray-700">Update registered agent information</span>
                  </li>
                </ul>
              </div>
            </section>

            {/* Business Name Availability */}
            <section className="mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-6">Business Name Availability Search</h2>
              <div className="bg-gray-50 rounded-2xl p-8">
                <p className="text-gray-700 leading-relaxed mb-6">
                  Before filing your formation documents, you must ensure your desired business name is available and complies with Texas naming requirements.
                </p>
                
                <h3 className="text-xl font-bold text-gray-900 mb-4">Name Requirements</h3>
                <div className="space-y-4 mb-6">
                  <div className="bg-white rounded-xl p-6 border-l-4 border-[#2C5F4F]">
                    <h4 className="font-bold text-gray-900 mb-2">Must Be Distinguishable</h4>
                    <p className="text-sm text-gray-600">Your business name must be distinguishable from existing entities registered in Texas</p>
                  </div>
                  <div className="bg-white rounded-xl p-6 border-l-4 border-[#2C5F4F]">
                    <h4 className="font-bold text-gray-900 mb-2">Include Entity Designation</h4>
                    <p className="text-sm text-gray-600">LLCs must include "Limited Liability Company," "LLC," or "L.L.C." Corporations must include "Corporation," "Incorporated," "Company," or abbreviations</p>
                  </div>
                  <div className="bg-white rounded-xl p-6 border-l-4 border-[#2C5F4F]">
                    <h4 className="font-bold text-gray-900 mb-2">Avoid Restricted Words</h4>
                    <p className="text-sm text-gray-600">Certain words like "bank," "insurance," "university" require special approval</p>
                  </div>
                </div>
                
                <div className="bg-[#FFE8D6] rounded-xl p-6">
                  <p className="text-sm text-gray-800">
                    <strong>Pro Tip:</strong> Use the SOSDirect Taxable Entity Search to check name availability before filing. You can also reserve a business name for 120 days by filing a Name Reservation form.
                  </p>
                </div>
              </div>
            </section>

            {/* Registered Agent Requirements */}
            <section className="mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-6">Registered Agent Requirements</h2>
              <div className="bg-gray-50 rounded-2xl p-8">
                <p className="text-gray-700 leading-relaxed mb-6">
                  Every business entity in Texas must maintain a registered agent with a physical street address in Texas. The registered agent receives legal documents and official correspondence on behalf of your business.
                </p>
                
                <h3 className="text-xl font-bold text-gray-900 mb-4">Registered Agent Must:</h3>
                <ul className="space-y-3 mb-6">
                  <li className="flex items-start space-x-3">
                    <div className="w-6 h-6 bg-[#2C5F4F] rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                      <i className="ri-check-line text-white text-sm"></i>
                    </div>
                    <span className="text-gray-700">Have a physical street address in Texas (no P.O. boxes)</span>
                  </li>
                  <li className="flex items-start space-x-3">
                    <div className="w-6 h-6 bg-[#2C5F4F] rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                      <i className="ri-check-line text-white text-sm"></i>
                    </div>
                    <span className="text-gray-700">Be available during normal business hours to accept service of process</span>
                  </li>
                  <li className="flex items-start space-x-3">
                    <div className="w-6 h-6 bg-[#2C5F4F] rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                      <i className="ri-check-line text-white text-sm"></i>
                    </div>
                    <span className="text-gray-700">Be an individual resident of Texas or a business entity authorized to do business in Texas</span>
                  </li>
                  <li className="flex items-start space-x-3">
                    <div className="w-6 h-6 bg-[#2C5F4F] rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                      <i className="ri-check-line text-white text-sm"></i>
                    </div>
                    <span className="text-gray-700">Consent to serve as registered agent for your entity</span>
                  </li>
                </ul>
                
                <p className="text-sm text-gray-600">
                  You can serve as your own registered agent, designate an officer or employee, or hire a professional registered agent service.
                </p>
              </div>
            </section>

            {/* Filing Fees */}
            <section className="mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-6">Filing Fees</h2>
              <div className="bg-gray-50 rounded-2xl p-8">
                <p className="text-gray-700 leading-relaxed mb-6">
                  Filing fees vary depending on the type of entity and document being filed. All fees are non-refundable.
                </p>
                
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b-2 border-gray-300">
                        <th className="text-left py-3 px-4 font-bold text-gray-900">Document Type</th>
                        <th className="text-left py-3 px-4 font-bold text-gray-900">Filing Fee</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      <tr>
                        <td className="py-3 px-4 text-gray-700">Certificate of Formation - LLC</td>
                        <td className="py-3 px-4 text-gray-700">$300</td>
                      </tr>
                      <tr>
                        <td className="py-3 px-4 text-gray-700">Certificate of Formation - Corporation</td>
                        <td className="py-3 px-4 text-gray-700">$300</td>
                      </tr>
                      <tr>
                        <td className="py-3 px-4 text-gray-700">Certificate of Formation - Partnership</td>
                        <td className="py-3 px-4 text-gray-700">$750</td>
                      </tr>
                      <tr>
                        <td className="py-3 px-4 text-gray-700">Amendment to Certificate of Formation</td>
                        <td className="py-3 px-4 text-gray-700">$150</td>
                      </tr>
                      <tr>
                        <td className="py-3 px-4 text-gray-700">Certificate of Termination</td>
                        <td className="py-3 px-4 text-gray-700">$40</td>
                      </tr>
                      <tr>
                        <td className="py-3 px-4 text-gray-700">Name Reservation</td>
                        <td className="py-3 px-4 text-gray-700">$40</td>
                      </tr>
                      <tr>
                        <td className="py-3 px-4 text-gray-700">Registered Agent Change</td>
                        <td className="py-3 px-4 text-gray-700">$15</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
                
                <div className="mt-6 bg-[#FFE8D6] rounded-xl p-6">
                  <p className="text-sm text-gray-800">
                    <strong>Note:</strong> Expedited processing is available for an additional fee. Same-day processing costs $25-$100 depending on the document type.
                  </p>
                </div>
              </div>
            </section>

            {/* Contact Information */}
            <section className="mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-6">Contact Information</h2>
              <div className="bg-gray-50 rounded-2xl p-8">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <h3 className="font-bold text-gray-900 mb-3">Business & Public Filings Division</h3>
                    <p className="text-sm text-gray-700 mb-2">Phone: (512) 463-5555</p>
                    <p className="text-sm text-gray-700 mb-2">Fax: (512) 463-5709</p>
                    <p className="text-sm text-gray-700">Email: corpinfo@sos.texas.gov</p>
                  </div>
                  <div>
                    <h3 className="font-bold text-gray-900 mb-3">Mailing Address</h3>
                    <p className="text-sm text-gray-700">
                      Texas Secretary of State<br />
                      P.O. Box 13697<br />
                      Austin, TX 78711-3697
                    </p>
                  </div>
                </div>
              </div>
            </section>

            {/* CTA Section */}
            <div className="bg-gradient-to-br from-[#1B3A3A] to-[#2C5F4F] rounded-3xl p-12 text-center">
              <h2 className="text-4xl font-bold text-white mb-4">Need Help with Business Registration?</h2>
              <p className="text-xl text-white/90 mb-8 max-w-2xl mx-auto">
                Our team can guide you through the Texas Secretary of State filing process and ensure your business is properly registered.
              </p>
              <Link
                to="/contact"
                className="inline-block bg-white text-[#1B3A3A] px-10 py-4 rounded-xl text-lg font-semibold hover:bg-[#FFE8D6] transition-all cursor-pointer whitespace-nowrap"
              >
                Contact Us for Assistance
              </Link>
            </div>
          </div>
        </main>

        <Footer />
      </div>
    </>
  );
}